import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const HowToIdentifyFH = () => {
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const videoContent = [
    {
      title: 'Recognizing Physical Signs of FH',
      duration: '8:42',
      views: '18K views',
      thumbnail:
        'https://public.readdy.ai/ai/img_res/b154f0978ce290aff426b919a8f5dbe9.jpg',
    },
    {
      title: 'Understanding Your Lipid Panel Results',
      duration: '12:10',
      views: '24K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Doctor%20explaining%20blood%20test%20lipid%20panel%20cholesterol%20results%20to%20patient%20on%20tablet%20screen%20in%20modern%20cardiology%20office%20professional%20healthcare%20consultation%20warm%20lighting%20clean%20background&width=400&height=250&seq=idfh-vid-thumb2&orientation=landscape',
    },
    {
      title: 'Family Screening: A Step-by-Step Guide',
      duration: '10:35',
      views: '15K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Multigenerational%20family%20at%20medical%20clinic%20for%20cardiovascular%20screening%20with%20nurse%20drawing%20blood%20warm%20professional%20healthcare%20photography%20modern%20hospital%20setting%20natural%20lighting&width=400&height=250&seq=idfh-vid-thumb3&orientation=landscape',
    },
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Familial Heart Disease Awareness
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              How to Identify Familial<br />Hypercholesterolemia
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Familial Hypercholesterolemia often goes undetected for decades. Learn the clinical signs, diagnostic criteria, and screening strategies that can help you and your family identify this inherited condition before it leads to heart disease.
            </p>
          </div>

          {/* Content Type Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Blog Card */}
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Cardiologist%20reviewing%20family%20medical%20history%20chart%20with%20patient%20pointing%20at%20hereditary%20heart%20disease%20risk%20factors%20on%20clipboard%20in%20warm%20modern%20medical%20office%20professional%20healthcare%20photography%20natural%20lighting&width=400&height=300&seq=idfh-blog-card-001&orientation=landscape"
                  alt="Blog articles about identifying FH"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'blog' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-article-line text-xl transition-colors ${
                      activeContentType === 'blog' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth guide to recognizing and diagnosing FH</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'blog' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Video Card */}
            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Medical%20professional%20performing%20cardiac%20ultrasound%20echocardiogram%20on%20patient%20in%20modern%20cardiology%20diagnostic%20center%20with%20heart%20imaging%20on%20screen%20professional%20healthcare%20photography%20warm%20lighting&width=400&height=300&seq=idfh-video-card-001&orientation=landscape"
                  alt="Educational videos about identifying FH"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'videos' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-video-line text-xl transition-colors ${
                      activeContentType === 'videos' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Video</h3>
                <p className="text-gray-500 text-sm">Visual guides on FH diagnosis</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'videos' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Infograph Card */}
            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Colorful%20medical%20diagnostic%20flowchart%20infographic%20about%20familial%20hypercholesterolemia%20identification%20criteria%20displayed%20on%20large%20screen%20with%20cholesterol%20level%20charts%20and%20family%20tree%20diagram%20modern%20data%20visualization%20coral%20and%20teal%20accents&width=400&height=300&seq=idfh-infograph-card-001&orientation=landscape"
                  alt="Infographics about identifying FH"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-bar-chart-box-line text-xl transition-colors ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Diagnostic criteria &amp; screening visuals</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Is Your "High Cholesterol" Actually Genetic? 5 Surprising Clues You Can't Ignore
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Jan 15, 2025
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  10 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Doctor%20analyzing%20genetic%20test%20results%20and%20cholesterol%20blood%20work%20with%20patient%20in%20modern%20cardiology%20clinic%20showing%20DNA%20helix%20and%20lipid%20panel%20charts%20professional%20medical%20consultation%20warm%20lighting%20clean%20aesthetic&width=800&height=400&seq=idfh-blog-hero-genetic&orientation=landscape"
                  alt="Genetic High Cholesterol"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  When you hear "high cholesterol," your mind probably jumps to diet, exercise, and lifestyle choices. For most people, that's a big part of the picture. We're taught that managing cholesterol is about eating right and staying active, and while that's excellent advice, it doesn't tell the whole story.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For a significant number of people, dangerously high cholesterol has little to do with lifestyle—it's written in their DNA. This condition is called <strong>Familial Hypercholesterolemia (FH)</strong>, and it's a common but widely underdiagnosed genetic disorder. This article will uncover five surprising clues that could indicate you or your family members are at risk, moving beyond the usual advice to reveal the signs hidden in your health numbers, your family tree, and even on your own body.
                </p>
              </div>

              {/* Clue 1 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It's Not Just High—It's Extremely High
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The first and most direct clue is an LDL cholesterol level that isn't just high—it's in a completely different league. For adults with FH, the LDL level is typically <strong>190 mg/dL (or 4.9 mmol/L) or higher</strong>, while for children and teens, a reading of <strong>160 mg/dL (or 4.1 mmol/L) or higher</strong> is a major red flag.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-[#fef5f5] rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <i className="ri-user-line text-[#f04e58] text-xl"></i>
                        <h4 className="font-semibold text-gray-900">Adults</h4>
                      </div>
                      <p className="text-3xl font-bold text-[#f04e58] mb-1">&gt; 190 mg/dL</p>
                      <p className="text-sm text-gray-600">4.9 mmol/L or higher</p>
                    </div>
                    <div className="p-4 bg-[#f0fdf4] rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <i className="ri-user-smile-line text-[#0d9488] text-xl"></i>
                        <h4 className="font-semibold text-gray-900">Children</h4>
                      </div>
                      <p className="text-3xl font-bold text-[#0d9488] mb-1">&gt; 160 mg/dL</p>
                      <p className="text-sm text-gray-600">4.1 mmol/L or higher</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  These levels are so high because the body's natural system for clearing LDL cholesterol from the blood is genetically impaired from birth, meaning the numbers are often elevated from a very young age.
                </p>
              </div>

              {/* Clue 2 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Early Heart Disease in Your Family Is a Major Red Flag
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Beyond the numbers on your lab report, your family tree holds the next set of critical clues. Because FH is genetic, it leaves a distinct pattern across generations. A key indicator is a history of premature cardiovascular events in your family.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58] mb-4">
                  <p className="text-gray-900 font-semibold mb-3">Early Heart Disease Defined:</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <i className="ri-heart-pulse-line text-[#f04e58] text-xl mt-0.5 flex-shrink-0"></i>
                      <p className="text-gray-700 leading-relaxed">
                        A heart attack, stent, or bypass surgery in <strong>men younger than 55</strong>
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-heart-pulse-line text-[#f04e58] text-xl mt-0.5 flex-shrink-0"></i>
                      <p className="text-gray-700 leading-relaxed">
                        A heart attack, stent, or bypass surgery in <strong>women younger than 60</strong>
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-group-line text-[#0d9488] text-xl mt-0.5 flex-shrink-0"></i>
                      <p className="text-gray-700 leading-relaxed">
                        A general family reputation for "high cholesterol" that seems to affect many members
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  If you know of relatives who experienced heart problems at a young age, it's a significant clue that FH may be running through your family line.
                </p>
              </div>

              {/* Clue 3 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Clues Can Be Skin-Deep
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While your family's past holds vital clues, your own body might be offering physical evidence you can see today. Surprisingly, a lifetime of extremely high cholesterol can sometimes cause visible physical signs. While not everyone with FH will have them, their presence is a strong clue that should not be ignored.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#f04e58]">
                    <h4 className="font-semibold text-gray-900 mb-2">Xanthelasma</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Yellowish patches of cholesterol buildup that appear on or around the eyelids.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#0d9488]">
                    <h4 className="font-semibold text-gray-900 mb-2">Tendon Xanthomas</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Thickening or noticeable lumps over a person's Achilles tendon or the knuckles of their hands.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border-l-4 border-[#f04e58]">
                    <h4 className="font-semibold text-gray-900 mb-2">Corneal Arcus</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      A white or grey ring that forms around the outer edge of the cornea, especially when it appears in a young person.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488]">
                  <p className="text-gray-900 font-semibold mb-2">Important Note:</p>
                  <p className="text-gray-700 leading-relaxed">
                    While these are a "strong clue," they are <strong>not always present</strong> in people with FH. Many individuals with the condition never develop visible deposits.
                  </p>
                </div>
              </div>

              {/* Clue 4 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    A Negative Genetic Test Doesn't Automatically Rule It Out
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Even if you have no physical signs, the diagnostic journey has another surprising twist, especially when it comes to genetic testing. This may seem counter-intuitive, but a positive test that identifies a mutation in one of the common FH genes (LDLR, APOB, or PCSK9) provides a definite diagnosis. However, the reverse is not always true.
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6 text-white mb-4">
                  <p className="text-xl font-bold text-white text-center leading-relaxed">
                    "A negative test doesn't rule out FH if the clinical picture is strong."
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Science is still discovering all the genetic variations that can cause FH. This means that if a person has extremely high LDL and a clear family history of early heart disease, a diagnosis of FH can still be made based on that powerful clinical evidence, even without genetic confirmation.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488]">
                  <p className="text-gray-900 font-semibold mb-2">Key Insight:</p>
                  <p className="text-gray-700 leading-relaxed">
                    A clinical diagnosis is so powerful—and why it can be the key that protects your entire family. The clinical picture (cholesterol levels + family history + physical signs) can be enough for diagnosis.
                  </p>
                </div>
              </div>

              {/* Clue 5 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    5
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Finding One Case Is the Key to Finding Others
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This is why a clinical diagnosis is so powerful—and why it can be the key that protects your entire family. Once one person in a family is diagnosed with FH, it unlocks a public health strategy called <strong>"cascade screening."</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This process uses the first diagnosed person (known as the "index case") as a starting point to efficiently find other relatives who may be at risk.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-center">
                      <div className="w-32 h-32 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-3">
                        <span className="text-5xl font-bold text-[#f04e58]">50%</span>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">Chance for First-Degree Relatives</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-center leading-relaxed">
                    Each first-degree relative (parents, siblings, children) has a <strong>50% chance</strong> of having it.
                  </p>
                </div>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-6 text-white">
                  <p className="font-semibold mb-2 text-white">The Power of Cascade Screening:</p>
                  <p className="text-white/95 leading-relaxed">
                    By testing these close family members, doctors can identify individuals who have inherited FH before they ever develop symptoms or have a cardiac event. This is the fastest and most effective way to find "hidden FH" in a family, allowing for early treatment that can save lives.
                  </p>
                </div>
              </div>

              {/* Final Reflection */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] via-white to-[#faf5f8] rounded-2xl p-8 border-2 border-[#0d9488]/20">
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  Familial Hypercholesterolemia is much more than just "high cholesterol." It is a specific, inherited condition with clear, though often overlooked, signs. By looking beyond lifestyle factors to recognize extremely high LDL numbers, patterns of early heart disease in a family, physical signs, and the power of cascade screening, we can bring this hidden condition to light.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  Awareness is the first and most critical step toward getting an early diagnosis and effective treatment, which can dramatically reduce the risk of heart disease. Now that you know the signs, what might your family's health story be telling you?
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#f04e58]/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Don't Ignore the Clues
                </h3>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  High cholesterol isn't always about lifestyle. For millions of people, it's genetic—and the clues are hiding in plain sight. Extremely high LDL numbers, early heart disease in your family, visible cholesterol deposits, and the power of cascade screening all point to one truth: <strong>knowledge is the first step toward prevention.</strong>
                </p>
                <p className="text-gray-900 font-semibold text-lg">
                  If you recognize any of these clues in yourself or your family, don't wait. Talk to your doctor about FH screening today.
                </p>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Take Control of Your Heart Health</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  If you suspect FH based on these clues, our specialists can help you get the right tests and create a personalized prevention plan. Early detection can change everything.
                </p>
                <Link
                  to="/consultation"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Screening
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How to Identify FH: A Visual Guide
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this educational video to learn the key signs, symptoms, and diagnostic steps for identifying Familial Hypercholesterolemia in yourself and your family members.
              </p>
            </div>

            {/* Video Player Placeholder */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-black aspect-video mb-8">
              <video
                controls
                autoPlay
                className="w-full h-full"
                poster="https://readdy.ai/api/search-image?query=Medical%20educational%20video%20thumbnail%20showing%20doctor%20explaining%20familial%20hypercholesterolemia%20diagnosis%20process%20with%20heart%20model%20and%20cholesterol%20level%20chart%20in%20background%20modern%20cardiology%20office%20professional%20warm%20lighting%20cinematic%20look&width=800&height=450&seq=idfh-video-poster-main&orientation=landscape"
              >
                <source
                  src="https://rr4---sn-a5meknd6.googlevideo.com/videoplayback?expire=1771394575&ei=7zmVaZbYM5fOn88PrPbIgA0&ip=151.186.183.87&id=b064c1e950a5b8d2&itag=22&source=contrib_service_notebooklm&begin=0&requiressl=yes&xpc=EghoqJzIP3oBAQ==&rms=su,su&sc=yes&susc=nblm&app=fife&ic=1045&eaua=vlflJW24Q14&pcm2=yes&mime=video/mp4&vprv=1&rqh=1&dur=451.883&lmt=1769285304497410&txp=0011224&sparams=expire,ei,ip,id,itag,source,requiressl,xpc,susc,app,ic,eaua,pcm2,mime,vprv,rqh,dur,lmt&sig=AJEij0EwRgIhAJT9sFeue1aSF21S4YgrLak25rtkzEPY-baN7BiF250XAiEAyi5dV6K2BAo5Gw0aZsINXhBhqghCgx4zOWmgGPjpJ5k=&redirect_counter=1&cm2rm=sn-nx5zk7e&rrc=191&req_id=7fe28a86e978a3ee&cms_redirect=yes&cmsv=e&met=1771387376,&mh=bX&mm=34&mn=sn-a5meknd6&ms=ltu&mt=1771387222&mv=m&mvi=4&pl=26&lsparams=met,mh,mm,mn,ms,mv,mvi,pl,rms,sc&lsig=APaTxxMwRQIgM_uExATmwvi7cDfvkexGdQWRBO_Z58Bt9LGmIccz5j4CIQDfh6nFbhlbPObrL8q-2VzJwxh6QiSpdEkkkwVdDovUIg%3D%3D"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">FH Identification Series</h3>
                  <p className="text-sm text-gray-500">Duration: 7:31</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This video covers the physical warning signs of FH, how to interpret your cholesterol numbers, the importance of family history, clinical diagnostic criteria used by doctors, and the role of genetic testing in confirming a diagnosis.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Related Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How to Identify FH: A Visual Diagnostic Guide
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Use these visual guides to understand the signs, criteria, and screening pathways for Familial Hypercholesterolemia.
              </p>
            </div>

            {/* Main Infographic */}
            <div className="bg-[#fef9f9] rounded-2xl p-6 shadow-lg mb-12">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/d64efd84d450455901e80558a0c64831.png"
                  alt="A 7-Step Clinical Pathway for Identifying Familial Hypercholesterolemia (FH) - Initial Screening, Clinical Evaluation, Confirmation and Proactive Management"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Key Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-stethoscope-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Clinical Criteria</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  DLCN and Simon Broome scoring systems combine cholesterol levels, physical signs, and family history for accurate diagnosis.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-dna-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Genetic Confirmation</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Testing for LDLR, APOB, and PCSK9 mutations provides definitive diagnosis and unlocks family‑wide screening.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-team-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Cascade Screening</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  One diagnosis can protect an entire family. Each first-degree relative has a 50% chance of carrying the same FH mutation.
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default HowToIdentifyFH;
